import { createContext, useContext, type ReactNode } from 'react';
import { useApi, type Schema } from '@cdn/api';
import { LearnMore } from '@cdn/docs';
import { useI18n } from '@cdn/i18n';
import { useModel } from '@cdn/state';
import { Button, Notice, ProblemNotice } from '@cdn/ui';
import { Visit } from './visit';

const Context = createContext('');

/**
 * Supplies the origin of published delivery, used only when a ticket link is origin-relative
 */
export function VisitProvider({ delivery, children }: { delivery: string; children: ReactNode }) {
	return <Context.Provider value={delivery}>{children}</Context.Provider>;
}

/**
 * Opens a private application as a member of its organization, and keeps the membership confirmed while the
 * window stays open. A public application needs no ticket, so nothing is offered for it here.
 */
export function OpenApplication({ application }: { application: Schema.Application }) {
	const { t, term } = useI18n();
	const api = useApi();
	const delivery = useContext(Context);
	const visit = useModel(() => new Visit({ api, application: application.id, delivery }), [api, application.id, delivery]);
	if (application.visibility !== 'private') return null;

	return (
		<div className="space-y-3">
			<Button onClick={() => void visit.start()} busy={visit.state === 'opening'} disabled={visit.state === 'open'}>
				{t('visit.open')}
			</Button>
			<p className="bui-reading text-sm text-muted">
				{term('help.access.ticket')} <LearnMore page="cdn/delivery/private-access" />
			</p>
			<div role="status" className="space-y-2">
				{visit.state === 'open' && <Notice tone="success">{t('visit.kept')}</Notice>}
				{visit.state === 'ended' && visit.problem === undefined && !visit.unreachable && <Notice tone="info">{t('visit.ended')}</Notice>}
				{visit.blocked && <Notice tone="warning">{t('visit.blocked')}</Notice>}
				{visit.unreachable && <Notice tone="warning">{t('visit.unreachable')}</Notice>}
			</div>
			{visit.problem !== undefined && <ProblemNotice problem={visit.problem} />}
		</div>
	);
}
